import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { sendSupportEmail } from '@/lib/services/emailService';
import { useToast } from '@/hooks/use-toast';

const ContactSupport = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({ subject: '', message: '' });
  const { toast } = useToast();

  const validateForm = () => {
    const newErrors = { subject: '', message: '' };
    let isValid = true;

    if (!subject.trim()) {
      newErrors.subject = 'Subject is required';
      isValid = false;
    } else if (subject.length < 3) {
      newErrors.subject = 'Subject must be at least 3 characters';
      isValid = false;
    } else if (subject.length > 100) {
      newErrors.subject = 'Subject must be less than 100 characters';
      isValid = false;
    }

    if (!message.trim()) {
      newErrors.message = 'Message is required';
      isValid = false;
    } else if (message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
      isValid = false;
    } else if (message.length > 1000) {
      newErrors.message = 'Message must be less than 1000 characters';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      await sendSupportEmail({ subject, message });
      
      toast({
        title: "Message Sent",
        description: "We'll get back to you as soon as possible.",
      });
      
      // Reset form
      setIsOpen(false);
      setSubject('');
      setMessage('');
      setErrors({ subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Button 
        onClick={() => setIsOpen(true)}
        className={cn(
          "flex items-center gap-2 shadow-lg",
          "bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700",
          "text-gray-900 dark:text-gray-100"
        )}
        size="lg"
        variant="ghost"
      >
        <MessageCircle className="h-4 w-4" />
        Contact Support
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Contact Support</DialogTitle>
            <DialogDescription>
              Send us a message and we'll get back to you as soon as possible.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="subject" className="text-sm font-medium">
                Subject
              </label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  if (errors.subject) {
                    setErrors(prev => ({ ...prev, subject: '' }));
                  }
                }}
                placeholder="How can we help?"
                disabled={isLoading}
                className={errors.subject ? 'border-red-500' : ''}
              />
              {errors.subject && (
                <Alert variant="destructive" className="py-2">
                  <AlertDescription>{errors.subject}</AlertDescription>
                </Alert>
              )}
              <div className="text-xs text-gray-500">
                {subject.length}/100 characters
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="message" className="text-sm font-medium">
                Message
              </label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  if (errors.message) {
                    setErrors(prev => ({ ...prev, message: '' }));
                  }
                }}
                placeholder="Please describe your issue..."
                className={`h-32 ${errors.message ? 'border-red-500' : ''}`}
                disabled={isLoading}
              />
              {errors.message && (
                <Alert variant="destructive" className="py-2">
                  <AlertDescription>{errors.message}</AlertDescription>
                </Alert>
              )}
              <div className="text-xs text-gray-500">
                {message.length}/1000 characters
              </div>
            </div>

            <DialogFooter className="sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactSupport;